import { REQUIRED_VEHICLE_IMAGE_KINDS, type VehicleImageKind } from '@zuruflex/types';
import { cn } from '@zuruflex/ui';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { useId, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/** Mirrors ALLOWED_IMAGE_TYPES on the API, which refuses to sign anything else. */
export const ACCEPTED_IMAGE_TYPES = 'image/jpeg,image/png,image/webp,image/avif';

export function isRequiredKind(kind: VehicleImageKind): boolean {
  return REQUIRED_VEHICLE_IMAGE_KINDS.includes(kind);
}

/**
 * One named slot in the photo grid — "Front three-quarter", "Boot" — rather
 * than an anonymous pile of thumbnails.
 *
 * Naming the shot is the whole point: a partner photographing their car in a
 * car park has no reason to know that the picture that sells it is the
 * front-left corner at 45°, so the slot says so, and the gallery gets the
 * same set of pictures from every partner.
 */
export function PhotoSlot({
  kind,
  previewUrl,
  busy = false,
  disabled = false,
  onPick,
  onRemove,
  footer,
}: {
  kind: VehicleImageKind;
  /** Object URL for a file not yet uploaded, or the stored image's URL. */
  previewUrl?: string;
  busy?: boolean;
  disabled?: boolean;
  onPick: (file: File) => void;
  onRemove?: () => void;
  /** Extra thumbnails of the same kind, in the manage view. */
  footer?: ReactNode;
}) {
  const { t } = useTranslation(['partner', 'common']);
  const input = useRef<HTMLInputElement>(null);
  const describedBy = useId();
  const label = t(`common:vehicleImageKind.${kind}`);
  const required = isRequiredKind(kind);

  return (
    <div className="flex flex-col gap-1.5">
      <div
        className={cn(
          'relative aspect-video overflow-hidden rounded-field border',
          previewUrl ? 'border-border-default' : 'border-dashed border-border-default',
          required && !previewUrl && 'border-border-strong',
        )}
      >
        <button
          type="button"
          disabled={disabled || busy}
          aria-describedby={describedBy}
          aria-label={previewUrl ? `${label} — ${t('common:action.change')}` : label}
          onClick={() => input.current?.click()}
          className="size-full"
        >
          {previewUrl ? (
            <img src={previewUrl} alt="" className="size-full object-cover" />
          ) : (
            <span className="flex size-full flex-col items-center justify-center gap-1 text-text-muted hover:text-text">
              <ImagePlus className="size-5" />
              <span className="text-[11px] font-medium">{t('common:action.add')}</span>
            </span>
          )}
        </button>

        {previewUrl && onRemove ? (
          <button
            type="button"
            disabled={disabled || busy}
            aria-label={`${t('photos.remove')} — ${label}`}
            onClick={onRemove}
            className="absolute right-1 top-1 rounded-full bg-ink-950/70 p-1 text-ink-50 hover:bg-ink-950"
          >
            <X className="size-3.5" />
          </button>
        ) : null}

        {busy ? (
          <span className="absolute inset-0 flex items-center justify-center bg-overlay">
            <Loader2 className="size-5 animate-spin text-text-inverse" />
          </span>
        ) : null}

        <input
          ref={input}
          type="file"
          accept={ACCEPTED_IMAGE_TYPES}
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) onPick(file);
            // Cleared so picking the same file twice still fires.
            event.target.value = '';
          }}
        />
      </div>

      <p className="text-xs font-medium text-text">
        {label}
        {required ? (
          <span className="ml-0.5 text-danger" title={t('addCar.photoRequired')}>
            *
          </span>
        ) : null}
      </p>
      <p id={describedBy} className="text-[11px] leading-snug text-text-muted">
        {t(`common:vehicleImageHint.${kind}`)}
      </p>
      {footer}
    </div>
  );
}
